import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Undo2, ShieldCheck } from 'lucide-react';
import { hardwareApi, type DeviceUnitRow, type DeviceOrderRow } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';
import OverrideGate from './OverrideGate';

/**
 * DANGER ZONE — take this device back off an order.
 *
 * Two depths, and the difference is not cosmetic:
 *
 *  - RELEASE, for a device merely reserved. Nothing was done to it; it goes
 *    back on the shelf and the order goes back to short.
 *  - WIPE, for one already provisioned or configured. It also destroys the
 *    completed checklist and unlinks the terminal id, which is work somebody
 *    actually did — so it asks why, and asks the operator to type the device's
 *    tracking id first.
 *
 * THE SERVER DECIDES WHICH. This component only renders the right affordance:
 * the sets below mirror `unitRelease.ts` so the wording matches, but nothing
 * here is a guard. Ask for a release on a shipped device and the server refuses
 * it, whatever this file believes.
 */

/** Mirrors the server's RELEASABLE_STATES — for WORDING, never for permission. */
const RELEASABLE = new Set(['allocated', 'awaiting', 'processing', 'on_hold', 'backordered']);
/** Mirrors the server's WIPE_STATES. */
const WIPE = new Set(['provisioning', 'configured', 'packing']);

/**
 * WHICH ORDER, IN WORDS.
 *
 * `ord_ee3108cd76a6921f7ed65484` is not an order an operator recognises, and
 * this sentence is the last thing they read before destroying something. Orders
 * carry `orderNo` (`RB-1004382`) for exactly this; the uuid tail is the
 * fallback for orders placed before that field existed.
 */
const orderLabel = (o: DeviceOrderRow | null, orderId?: string): string => {
  if (o?.orderNo) return o.orderNo;
  if (!orderId) return 'its order';
  return `order ${orderId.replace(/^ord_/, '').slice(0, 8).toUpperCase()}`;
};

const UnitDangerZone: React.FC<{
  unit: DeviceUnitRow;
  onReleased: () => void;
}> = ({ unit, onReleased }) => {
  const [order, setOrder] = useState<DeviceOrderRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!unit.orderId) { setOrder(null); return undefined; }
    (async () => {
      try {
        const res = await hardwareApi.getOrder(unit.orderId!);
        if (!cancelled) setOrder(res.order || null);
      } catch { /* the label falls back to a short id rather than blocking */ }
    })();
    return () => { cancelled = true; };
  }, [unit.orderId]);

  // The unlock gate. Nothing here is a guard — the server refuses a release
  // without a verified override whatever this component shows — but an operator
  // should see the wall before they type into it.
  const [stage, setStage] = useState<'locked' | 'unlocked'>('locked');

  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tier = RELEASABLE.has(unit.state) ? 'release' : WIPE.has(unit.state) ? 'wipe' : 'refused';
  const label = unit.trackingId || unit.unitId;

  // A wipe destroys a checklist, so it takes the same two keys a destructive
  // action takes everywhere else: say why, and type the thing's name.
  const ready = tier === 'release'
    || (tier === 'wipe' && reason.trim().length > 0 && confirm.trim() === label);

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const res = await hardwareApi.releaseUnit(unit.unitId, reason.trim() || undefined);
      toastService.success(res.tier === 'wipe'
        ? `${label} wiped and returned to stock.`
        : `${label} released back to stock.`);
      setReason(''); setConfirm('');
      // Straight back behind the wall: the unlock authorised THIS change, and
      // the server has already spent it.
      setStage('locked');
      onReleased();
    } catch (e: any) {
      setError(e?.message || 'Could not release this device.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-red-200 bg-white">
      <div className="flex items-center gap-2 border-b border-red-100 bg-[#FFF1F3] px-4 py-2.5">
        <AlertTriangle size={15} className="text-[#DC143C]" />
        <h3 className="text-[12.5px] font-bold text-[#9F1239]">Danger zone</h3>
      </div>

      <div className="p-4">
        {tier === 'refused' ? (
          <>
            <p className="text-[13px] font-semibold text-stone-800">
              This device cannot be returned to stock from here.
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-stone-500">
              It is <b className="text-stone-700">{unit.state.replace(/_/g, ' ')}</b>.
              {['shipped', 'delivered', 'deployed'].includes(unit.state)
                ? ' It has already left us — record what happened by setting it "returned" or "rma" instead.'
                : ' A device in this state re-enters stock through inspection, not through allocation.'}
            </p>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold text-stone-800">
              {tier === 'wipe' ? 'Wipe configuration and return to stock' : 'Unallocate — return to stock'}
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-stone-500">
              {tier === 'wipe' ? (
                <>
                  Clears this device&rsquo;s completed provisioning checklist, its terminal link and its box,
                  takes it off <b className="text-stone-700">{orderLabel(order, unit.orderId)}</b>, and puts it
                  back on the shelf. The checklist is not recoverable.
                </>
              ) : (
                <>
                  Takes this device off <b className="text-stone-700">{orderLabel(order, unit.orderId)}</b> and
                  puts it back on the shelf. Nothing has been configured yet, so nothing is lost — the order
                  simply goes back to short.
                </>
              )}
            </p>

            {order && (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-2 text-[11.5px]">
                <dt className="font-bold uppercase tracking-wide text-stone-400">Order</dt>
                <dd className="font-mono text-stone-700">{orderLabel(order, unit.orderId)}</dd>
                <dt className="font-bold uppercase tracking-wide text-stone-400">Status</dt>
                <dd className="capitalize text-stone-700">{(order.status || '—').replace(/_/g, ' ')}</dd>
                <dt className="font-bold uppercase tracking-wide text-stone-400">Devices</dt>
                <dd className="text-stone-700">{order.unitIds?.length ?? 0} on this order</dd>
              </dl>
            )}

            {/* The terminal record belongs to the POS side and outlives any one
                piece of hardware; saying so here stops an operator assuming a
                wipe unregistered the device downstream. */}
            {tier === 'wipe' && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11.5px] leading-relaxed text-amber-800">
                This unlinks our inventory record from terminal{' '}
                <b className="font-mono">{unit.terminalId || '—'}</b>. It does not unregister the terminal
                itself or remove its reporting history — those live with the POS services.
              </p>
            )}

            {/* THE GATE. Everything below it is inert until a code has been
                verified — including a device allocated by mistake seconds ago.
                That is stricter than gating only configured devices, and it is
                the point: which physical device a customer receives is not
                something one person changes on a whim.

                Shared with the unit picker's swap, so there is ONE code-entry
                behaviour rather than two that drift. */}
            {stage !== 'unlocked' ? (
              <div className="mt-3">
                <OverrideGate
                  unitId={unit.unitId}
                  intent={tier === 'wipe' ? 'Wiping this device' : 'Unallocating this device'}
                  onUnlocked={() => setStage('unlocked')}
                />
              </div>
            ) : (
              <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-[11.5px] font-medium text-emerald-800">
                <ShieldCheck size={12} /> Unlocked. This device can be changed once — it re-locks after.
              </p>
            )}

            {stage === 'unlocked' && tier === 'wipe' && (
              <div className="mt-3 space-y-2.5">
                <div>
                  <label htmlFor="dz-reason" className="mb-1 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
                    Why is this being wiped?
                  </label>
                  <input
                    id="dz-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Configured against the wrong order…"
                    className="min-h-[40px] w-full rounded-lg border border-stone-300 px-2.5 text-[12.5px] outline-none focus:border-[#DC143C]"
                  />
                </div>
                <div>
                  <label htmlFor="dz-confirm" className="mb-1 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
                    Type <span className="font-mono text-stone-700">{label}</span> to confirm
                  </label>
                  <input
                    id="dz-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={label}
                    className="min-h-[40px] w-full rounded-lg border border-stone-300 px-2.5 font-mono text-[12.5px] outline-none focus:border-[#DC143C]"
                  />
                </div>
              </div>
            )}

            {error && <p className="mt-2 text-[12px] font-medium text-[#DC143C]">{error}</p>}

            {stage === 'unlocked' && (
            <button
              onClick={run}
              disabled={!ready || busy}
              className="mt-3 inline-flex min-h-[42px] items-center gap-2 rounded-lg px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
              style={{ background: '#DC143C' }}
            >
              {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <Undo2 size={14} />}
              {tier === 'wipe' ? 'Wipe and return to stock' : 'Unallocate this device'}
            </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UnitDangerZone;
